import { PlayIcon } from '@components/Icons/PlayIcon';
import NapsterSpinner from '@components/NapsterSpinner';
import { PlaylistItem } from '@store/reducers/commonStore';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

interface PlaylistCardProps {
  playlist: PlaylistItem;
  playlistType: 'music' | 'serenity' | 'book' | 'creation';
  thumbnailUrl: string;
  playingPlaylistId: string | null;
  isDeleting: boolean;
  onPlay: (playlist: PlaylistItem) => void | Promise<void>;
  onEdit: (id: string, name: string, description: string) => void;
  onDelete: (
    id: string,
    name: string,
    type: 'music' | 'serenity' | 'book' | 'creation',
  ) => void;
}

export const PlaylistCard = memo(
  ({
    playlist,
    playlistType,
    thumbnailUrl,
    playingPlaylistId,
    isDeleting,
    onPlay,
    onEdit,
    onDelete,
  }: PlaylistCardProps) => {
    const { t } = useTranslation();
    return (
      <div className="flex flex-col cursor-pointer">
        <div className="relative w-full h-[520px] rounded-[16px] overflow-hidden shadow-[0_4px_16px_0_rgba(0,0,0,0.40)] hover:shadow-[0_4px_20px_0_rgba(0,0,0,0.50)] transition-all duration-300 group">
          <img
            src={thumbnailUrl}
            alt={playlist.name}
            className="w-full h-full object-cover"
            onClick={() => onPlay(playlist)}
          />

          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_34.69%,rgba(0,0,0,0.9)_100%)] z-[99] rounded-[16px]" />

          <div className="absolute inset-0 bg-black/40 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2 z-[100]">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPlay(playlist);
              }}
              disabled={playingPlaylistId === playlist.id}
              className="flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors duration-200 disabled:opacity-50">
              {playingPlaylistId === playlist.id ? (
                <NapsterSpinner size="small" />
              ) : (
                <PlayIcon width={20} height={20} />
              )}
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(playlist.id, playlist.name, playlist.description);
              }}
              className="flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors duration-200">
              <i className="fas fa-edit text-white text-lg" />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(playlist.id, playlist.name, playlistType);
              }}
              disabled={isDeleting}
              className="flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors duration-200 disabled:opacity-50">
              <i className="fas fa-trash text-white text-lg" />
            </button>
          </div>

          <div className="absolute z-[100] bottom-0 left-0 p-5 flex flex-col gap-2 w-full text-left">
            <div
              title={playlist.name}
              className="text-white line-clamp-2 font-[DM_Serif_Display] font-normal text-[20px] md:text-[24px] leading-[28px]">
              {playlist.name}
            </div>
            <div className="text-white/70 text-sm leading-[140%] line-clamp-1">
              {t('playlists.trackCount', { count: playlist.trackCount || 0 })}
            </div>
          </div>
        </div>
      </div>
    );
  },
);

PlaylistCard.displayName = 'PlaylistCard';
